import React, { useState } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { Event } from '@/types/social';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import ImageUploader from '../ImageUploader';
import { removeImage } from '@/utils/imageUtils';
import { useAuth } from '@/context/AuthContext';

interface EventFormProps {
  editingEvent: Event | null;
  onCancel: () => void;
  onSave: () => void;
}

const EventForm: React.FC<EventFormProps> = ({
  editingEvent,
  onCancel,
  onSave
}) => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({
    name: editingEvent?.name || '',
    description: editingEvent?.description || '',
    location: editingEvent?.location || '',
    start_date: editingEvent?.start_date ? new Date(editingEvent.start_date).toISOString().split('T')[0] : '',
    start_time: editingEvent?.start_date ? new Date(editingEvent.start_date).toISOString().split('T')[1].substring(0, 5) : '',
    end_date: editingEvent?.end_date ? new Date(editingEvent.end_date).toISOString().split('T')[0] : '',
    end_time: editingEvent?.end_date ? new Date(editingEvent.end_date).toISOString().split('T')[1].substring(0, 5) : '',
    capacity: editingEvent?.capacity || '',
    is_public: editingEvent?.is_public ?? true,
    festi_pass_eligible: editingEvent?.festi_pass_eligible ?? false,
    festi_pass_category: editingEvent?.festi_pass_category || '',
    image_url: editingEvent?.image_url || ''
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      setFormValues(prev => ({ 
        ...prev, 
        [name]: (e.target as HTMLInputElement).checked 
      }));
    } else {
      setFormValues(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormValues(prev => ({ ...prev, [name]: checked }));
  };
  
  const handleImageUpload = (url: string) => {
    setFormValues(prev => ({ ...prev, image_url: url }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      // Validation
      if (!formValues.name || !formValues.location || !formValues.start_date || !formValues.end_date) {
        toast({
          title: t('admin.error'),
          description: t('admin.fillRequiredFields'),
          variant: 'destructive',
        });
        return;
      }
      
      // Combine date and time
      const startDateTime = new Date(`${formValues.start_date}T${formValues.start_time || '00:00'}`);
      const endDateTime = new Date(`${formValues.end_date}T${formValues.end_time || '23:59'}`);
      
      // Validate dates
      if (endDateTime <= startDateTime) {
        toast({
          title: t('admin.error'),
          description: t('admin.endDateMustBeAfterStart'),
          variant: 'destructive',
        });
        return;
      }
      
      // Prepare event data
      const eventData = {
        name: formValues.name,
        description: formValues.description,
        location: formValues.location,
        start_date: startDateTime.toISOString(),
        end_date: endDateTime.toISOString(),
        capacity: formValues.capacity ? parseInt(formValues.capacity as string) : null,
        is_public: formValues.is_public,
        festi_pass_eligible: formValues.festi_pass_eligible,
        festi_pass_category: formValues.festi_pass_category || null,
        image_url: formValues.image_url,
        organizer_id: user?.id
      };
      
      // If editing and the image has changed, remove the old image from storage
      if (editingEvent && editingEvent.image_url && 
          editingEvent.image_url !== formValues.image_url && 
          editingEvent.image_url.includes('events/')) {
        try {
          await removeImage(editingEvent.image_url, 'events');
        } catch (error) {
          console.error('Error removing old image:', error);
          // Don't block the form submission if image removal fails
        }
      }
      
      // Create or update event
      if (editingEvent) {
        const { error } = await supabase
          .from('events')
          .update(eventData)
          .eq('id', editingEvent.id);
          
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('events')
          .insert([eventData]);
          
        if (error) throw error;
      }
      
      toast({
        title: t('admin.success'),
        description: editingEvent 
          ? t('admin.eventUpdated', 'Event updated successfully') 
          : t('admin.eventCreated', 'Event created successfully'),
      });
      
      onSave();
      
    } catch (error) {
      console.error('Error saving event:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorSavingEvent'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          {editingEvent 
            ? t('admin.editEvent', 'Edit Event') 
            : t('admin.createEvent', 'Create Event')}
        </CardTitle>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onCancel}
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t('admin.eventBasicInfo', 'Basic Information')}</h3>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">{t('admin.eventName', 'Event Name')}<span className="text-destructive">*</span></Label>
                <Input
                  id="name"
                  name="name"
                  placeholder={t('admin.enterEventName', 'Enter event name')}
                  value={formValues.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location">{t('admin.location', 'Location')}<span className="text-destructive">*</span></Label>
                <Input
                  id="location"
                  name="location"
                  placeholder={t('admin.enterLocation', 'Enter location')}
                  value={formValues.location}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">{t('admin.description', 'Description')}</Label>
              <Textarea
                id="description"
                name="description"
                placeholder={t('admin.enterDescription', 'Enter event description')}
                value={formValues.description}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="start_date">{t('admin.startDate', 'Start Date')}<span className="text-destructive">*</span></Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    id="start_date"
                    name="start_date"
                    type="date"
                    value={formValues.start_date}
                    onChange={handleInputChange}
                    required
                  />
                  <Input
                    id="start_time"
                    name="start_time"
                    type="time"
                    value={formValues.start_time}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="end_date">{t('admin.endDate', 'End Date')}<span className="text-destructive">*</span></Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    id="end_date"
                    name="end_date"
                    type="date"
                    value={formValues.end_date}
                    onChange={handleInputChange}
                    required
                  />
                  <Input
                    id="end_time"
                    name="end_time"
                    type="time"
                    value={formValues.end_time}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="capacity">{t('admin.capacity', 'Capacity')}</Label>
                <Input
                  id="capacity"
                  name="capacity"
                  type="number"
                  min="0"
                  placeholder={t('admin.enterCapacity', 'Enter capacity')}
                  value={formValues.capacity}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="space-y-2 pt-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_public"
                    checked={formValues.is_public}
                    onCheckedChange={(checked) => handleSwitchChange('is_public', checked)}
                  />
                  <Label htmlFor="is_public">{t('admin.isPublic', 'Public Event')}</Label>
                </div>
              </div>
            </div>
          </div>
          
          {/* FestiPass Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t('admin.festiPassDetails', 'FestiPass Details')}</h3>
            
            <div className="flex items-center space-x-2 mb-4">
              <Switch
                id="festi_pass_eligible"
                checked={formValues.festi_pass_eligible}
                onCheckedChange={(checked) => handleSwitchChange('festi_pass_eligible', checked)}
              />
              <Label htmlFor="festi_pass_eligible">{t('admin.festiPassEligible', 'FestiPass Eligible')}</Label>
            </div>
            
            {formValues.festi_pass_eligible && (
              <div className="space-y-2">
                <Label htmlFor="festi_pass_category">{t('admin.festiPassCategory', 'FestiPass Category')}</Label>
                <Input
                  id="festi_pass_category"
                  name="festi_pass_category"
                  placeholder={t('admin.enterCategory', 'Enter category')}
                  value={formValues.festi_pass_category}
                  onChange={handleInputChange}
                />
              </div>
            )}
          </div>
          
          {/* Image Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t('admin.eventImage', 'Event Image')}</h3>
            
            <ImageUploader
              existingUrl={formValues.image_url}
              onUpload={handleImageUpload}
              accept="image/*"
              bucketName="events"
            />
          </div>
          
          {/* Form Actions */}
          <div className="flex justify-end space-x-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
            >
              {t('admin.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {editingEvent 
                ? t('admin.update', 'Update') 
                : t('admin.create', 'Create')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default EventForm;
